import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Which physical station is this screen?
 *
 * Before this, floor screens sent a hardcoded `${department.toUpperCase()}-1`, so
 * Wicking 1 and Wicking 2 -- two separate PCs loading the identical kiosk URL --
 * both recorded WICKING-1 and per-station measurement was impossible.
 *
 * The station comes from `?station=WICK-2` on the URL. The StationAgent already sets
 * each kiosk's URL per machine (stations.kiosk_url), so this needs no new plumbing
 * on the floor: set the param once per station in the station-manager dashboard.
 *
 * The code is cached in localStorage so an operator navigating inside the app keeps
 * their station, and a station override is sticky to the machine.
 */

const STORAGE_KEY = 'stationCode';
const SESSION_KEY = 'stationSessionId';

const DEPARTMENT_DEFAULT = {
  carting: 'CART-1',
  wicking: 'WICK-1',
  pouring: 'POUR-1',
  packaging: 'PACK-1'
};

export default function useStationIdentity(department) {
  const [stationCode, setStationCode] = useState(null);
  const [stationName, setStationName] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isExplicit, setIsExplicit] = useState(false);
  const sessionIdRef = useRef(null);

  // Resolve the station once on mount: URL param wins, then the sticky value for
  // this machine, then the department's station 1 as a last resort.
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('station');
    if (fromUrl) {
      const code = fromUrl.trim().toUpperCase();
      localStorage.setItem(STORAGE_KEY, code);
      setStationCode(code);
      setIsExplicit(true);
      return;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setStationCode(saved);
      setIsExplicit(true);
      return;
    }
    setStationCode(DEPARTMENT_DEFAULT[department] || null);
    setIsExplicit(false);
  }, [department]);

  // Friendly name, and a check that the code is real.
  useEffect(() => {
    if (!stationCode) return;
    let cancelled = false;
    axios
      .get('/api/station-floor/stations')
      .then(({ data }) => {
        if (cancelled) return;
        const hit = (data || []).find((s) => s.station_code === stationCode);
        setStationName(hit ? hit.station_name : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [stationCode]);

  // Recover a session this browser already opened (kiosk reload, refresh).
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (!saved || !stationCode) return;
    axios
      .get(`/api/station-floor/session?station=${encodeURIComponent(stationCode)}`)
      .then(({ data }) => {
        if (data && data.id === saved) {
          setSessionId(data.id);
          sessionIdRef.current = data.id;
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      })
      .catch(() => localStorage.removeItem(SESSION_KEY));
  }, [stationCode]);

  /** Open a server-side session. Login must not fail if this does. */
  const openSession = useCallback(
    async (operator, loginMethod = 'pin') => {
      if (!stationCode || !operator) return null;
      try {
        const { data } = await axios.post('/api/station-floor/session', {
          station_code: stationCode,
          department,
          operator_name: operator.display_name,
          operator_id: operator.id ?? null,
          login_method: loginMethod
        });
        if (data?.id) {
          localStorage.setItem(SESSION_KEY, data.id);
          setSessionId(data.id);
          sessionIdRef.current = data.id;
          return data.id;
        }
      } catch (err) {
        console.error('[station] could not open session:', err.message);
      }
      return null;
    },
    [stationCode, department]
  );

  const closeSession = useCallback(async (reason = 'logout') => {
    const id = sessionIdRef.current || localStorage.getItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    sessionIdRef.current = null;
    if (!id) return;
    try {
      await axios.post(`/api/station-floor/session/${id}/end`, { reason });
    } catch (err) {
      console.error('[station] could not close session:', err.message);
    }
  }, []);

  /** Spread onto a scan payload so every scan carries station + operator + session. */
  const scanContext = useCallback(
    (operator) => ({
      stationCode,
      operatorId: operator?.id ?? null,
      sessionId: sessionIdRef.current || null
    }),
    [stationCode]
  );

  return {
    stationCode,
    stationName,
    sessionId,
    /** false means the station was guessed from the department, not configured. */
    isExplicit,
    openSession,
    closeSession,
    scanContext,
    setStationCode: (code) => {
      const c = String(code || '').trim().toUpperCase();
      if (!c) return;
      localStorage.setItem(STORAGE_KEY, c);
      setStationCode(c);
      setIsExplicit(true);
    }
  };
}
